import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FaShoppingCart, FaBolt, FaArrowLeft } from 'react-icons/fa';
import { getProduct, addToCart } from '../services/api';
import './ProductDetail.css';

function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addedToCart, setAddedToCart] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const data = await getProduct(id);
        console.log('[ProductDetail] Raw product data:', data);
        
        // Handle different response structures
        let productData = data;
        if (data?.data) {
          productData = data.data;
        }
        
        setProduct(productData);
        
        // Set default variant if product has variants
        if (productData?.variants && productData.variants.length > 0) {
          setSelectedVariant(productData.variants[0].id);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch product:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const handleAddToCart = () => {
    if (product) {
      const variantId = product.variants && product.variants.length > 0 ? selectedVariant : null;
      addToCart(product, variantId, 1);
      setAddedToCart(true);
      setTimeout(() => setAddedToCart(false), 2000);
    }
  };

  const handleBuyNow = () => {
    if (product) {
      const variantId = product.variants && product.variants.length > 0 ? selectedVariant : null;
      addToCart(product, variantId, 1);
      navigate('/cart');
    }
  };

  const getCurrentPrice = () => {
    if (product?.variants && product.variants.length > 0 && selectedVariant) {
      const variant = product.variants.find(v => v.id === selectedVariant);
      return variant?.price || '0.00';
    }
    return product?.price || '0.00';
  };

  const getSelectedVariantStock = () => {
    if (product?.variants && product.variants.length > 0 && selectedVariant) {
      const variant = product.variants.find(v => v.id === selectedVariant);
      return variant?.stock || 0;
    }
    return product?.stock_count || 0;
  };

  if (loading) {
    return (
      <div className="product-detail-page">
        <div className="container">
          <div className="loading">
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="product-detail-page">
        <div className="container">
          <div className="error-container">
            <h2>Product Not Found</h2>
            <p>Sorry, we couldn't find that product.</p>
            <Link to="/products" className="btn btn-primary">Back to Products</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="product-detail-page">
      <div className="container">
        <Link to="/products" className="back-link">
          <FaArrowLeft /> Back to Products
        </Link>

        <div className="product-detail">
          <div className="product-media">
            <div className="product-main-image">
              {product.images && product.images.length > 0 ? (
                <img src={product.images[0].url} alt={product.name} className="product-image" />
              ) : (
                <div className="placeholder-image">
                  <p>Product Image Coming Soon</p>
                </div>
              )}
              <span className="status-badge working">Undetected - Working</span>
            </div>
          </div>

          <div className="product-details">
            <h1 className="product-title">{product.title || product.name || 'Product'}</h1>
            
            {product.variants && product.variants.length > 0 && (
              <div className="product-variants-section">
                <label htmlFor="variant-select">Select Variant:</label>
                <select 
                  id="variant-select"
                  value={selectedVariant} 
                  onChange={(e) => setSelectedVariant(parseInt(e.target.value))}
                  className="variant-select"
                >
                  {product.variants.map(variant => (
                    <option key={variant.id} value={variant.id}>
                      {variant.name} - ${parseFloat(variant.price || 0).toFixed(2)} 
                      {variant.stock > 0 ? ` (${variant.stock} in stock)` : ' (Out of stock)'}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="product-price-section">
              <span className="product-price">${parseFloat(getCurrentPrice()).toFixed(2)}</span>
              <span className="price-period">/ Lifetime</span>
              {getSelectedVariantStock() > 0 && (
                <span className="stock-indicator">{getSelectedVariantStock()} in stock</span>
              )}
            </div>

            <div className="product-actions">
              <button 
                className={`btn btn-primary btn-large ${addedToCart ? 'added' : ''}`}
                onClick={handleAddToCart}
                disabled={getSelectedVariantStock() === 0}
              >
                {addedToCart ? (
                  <>✓ Added to Cart</>
                ) : getSelectedVariantStock() === 0 ? (
                  <>Out of Stock</>
                ) : (
                  <><FaShoppingCart /> Add to Cart</>
                )}
              </button>
              <button 
                onClick={handleBuyNow} 
                className="btn btn-secondary btn-large"
                disabled={getSelectedVariantStock() === 0}
              >
                <FaBolt /> Buy Now
              </button>
            </div>

            <div className="product-description">
              <h3>Description</h3>
              <p>{product.description || 'No description available.'}</p>
            </div>

            {product.deliverables_type && (
              <div className="product-delivery-info">
                <h3>Delivery Type</h3>
                <p className="delivery-badge">{product.deliverables_type}</p>
              </div>
            )}

            <div className="product-features">
              <h3>Features</h3>
              <ul>
                <li>✓ Instant delivery after purchase</li>
                <li>✓ Free lifetime updates</li>
                <li>✓ 24/7 customer support</li>
                <li>✓ Built-in HWID spoofer</li>
                <li>✓ Regular detection protection updates</li>
              </ul>
            </div>

            <div className="product-info-box">
              <h4>Important Information</h4>
              <p>• Keys are locked to one HWID</p>
              <p>• Updates are automatic and free</p>
              <p>• Contact support for technical issues</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetail;
